import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtCompact, verdictLabel } from "@/lib/format";
import { Moon, ShieldCheck, XCircle, AlertTriangle, RefreshCw, Ban } from "lucide-react";

const TONE = {
  Compliant: { icon: ShieldCheck, cls: "text-emerald-600", badge: "bg-emerald-500/12 text-emerald-700" },
  Questionable: { icon: AlertTriangle, cls: "text-amber-600", badge: "bg-amber-500/15 text-amber-700" },
  "Non-Compliant": { icon: XCircle, cls: "text-red-600", badge: "bg-red-500/12 text-red-700" },
};

/** Live AAOIFI screening for one listing, computed from its filed accounts. */
export const ShariahScreenCard = ({ symbol }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/halal/screen/${encodeURIComponent(symbol)}`,
        { params: refresh ? { refresh: true } : {} });
      setRep(data);
    } catch (e) { console.error("Shariah screening unavailable:", e); }
    finally { setBusy(false); }
  }, [symbol]);

  useEffect(() => { load(false); }, [load]);

  if (!rep) return null;
  const tone = TONE[rep.status] || TONE.Questionable;
  const Icon = tone.icon;

  return (
    <Card className="p-5 shadow-premium" data-testid="shariah-screen-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Moon className="h-4 w-4 text-[color:var(--tharwy-indigo)]" />Shariah screening
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Icon className={`h-5 w-5 ${tone.cls}`} />
            <span className={`text-lg font-semibold ${tone.cls}`} data-testid="shariah-status">{verdictLabel(rep.status)}</span>
            <Badge variant="secondary" className="text-[10px]" data-testid="shariah-source">
              {rep.live ? "Live filings" : "Tharwy model"}
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => load(true)} disabled={busy}
          title="Refetch the latest filings" data-testid="shariah-refresh">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="mt-3 space-y-1.5" data-testid="shariah-checks">
        {(rep.checks || []).map((c) => (
          <div key={c.label} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-muted-foreground">{c.label}</span>
            <span className={`numeric font-medium ${c.unknown ? "text-muted-foreground" : c.pass ? "text-emerald-600" : "text-red-600"}`}>
              {c.value}
            </span>
          </div>
        ))}
      </div>

      {!!rep.purification_pct && (
        <div className="mt-3 rounded-lg bg-secondary/60 px-3 py-2 text-xs" data-testid="shariah-purification">
          <span className="font-medium">Purification: {rep.purification_pct}%</span> of any dividend
          from this holding should be given away.
        </div>
      )}

      {(rep.reasons || []).length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed" data-testid="shariah-reasons">
          {rep.reasons.join(" ")}
        </p>
      )}

      {rep.financials && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-muted-foreground"
          data-testid="shariah-financials">
          <div className="flex justify-between"><span>Market cap</span><span className="numeric">{fmtCompact(rep.financials.market_cap)}</span></div>
          <div className="flex justify-between"><span>Debt</span><span className="numeric">{fmtCompact(rep.financials.total_debt)}</span></div>
          <div className="flex justify-between"><span>Cash</span><span className="numeric">{fmtCompact(rep.financials.cash_and_investments)}</span></div>
          <div className="flex justify-between"><span>Revenue</span><span className="numeric">{fmtCompact(rep.financials.revenue)}</span></div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>{rep.methodology}</span>
        {rep.as_of && <span data-testid="shariah-as-of">· accounts as of {rep.as_of}</span>}
        {rep.islamic_account && !rep.allowed_on_islamic_account && (
          <Badge variant="secondary" className="gap-1 text-[10px] text-red-700 bg-red-500/12"
            data-testid="shariah-blocked-badge">
            <Ban className="h-3 w-3" /> Blocked on your Islamic account
          </Badge>
        )}
      </div>
    </Card>
  );
};
